import { FC, useCallback, useEffect, useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import axiosInstance from 'utils/axios';
import { PageProps } from 'utils/constants/data';
import { privileges } from 'utils/constants/privileges';
import { routes } from 'utils/constants/routes';
import { URLS } from 'utils/constants/urls';
import {
  filterDataforApi,
  pageNo,
  rowCount,
  setPageNo,
  setFilter,
  setFilterDataForApi,
  filterData,
  setRowCount,
} from 'utils/redux/reducer/filter-slice';
import {createDropdownData, downloadExportFile} from 'utils/validators/HelperFunctions';
import Notification from 'components/Notification';
import StyledTabs from 'components/StyledTabs';
import {
  AlertColor,
  Box,
  Grid,
  Tab,
  Tabs,
  Typography,
  SelectChangeEvent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import SalesCallFollowUpForm from './SalesCallFollowUpForm';
import strings from 'common/Translation/Translate';
import dayjs from 'dayjs';
import DrawerMenu from 'components/DrawerMenu';
import Delete from 'screens/Authenticated/CommonPage/Delete';
import { TimelineItem } from 'components/TimelineSection';
import CommentDrawer from 'components/CommentDrawer';
import SalesCallTimelineSection from 'components/SalesCallTimelineSection';
import { Privilege } from 'utils/redux/reducer/authentication-slice';
import TablePaginationAction from 'components/TablePaginationAction';
import SalesCallCard from './SalesCallCard';
import { useNavigate } from 'react-router-dom';
import ListPage from 'screens/Authenticated/CommonPage/ListPage';
import { PageConfigModel } from 'screens/Authenticated/CommonPage/ListPage';
import { BadgeOutlined, ChatBubbleOutline, Comment, GridViewOutlined, ViewListOutlined } from '@mui/icons-material';
import { SalesDetails } from './AddEditSalesCalls/SalesDetails';
import {
  formatSalesCallType,
  SalesCallAssigneeAvatar,
  SalesCallContactCell,
  SalesCallDateCell,
  SalesCallStatusChip,
} from './salesCallListDisplay';

interface SalesCallsListProps {
  tabValue?: number;
  assignees?: { label: string; value: string }[];
  enableCardView?: boolean;
  onTabChange?: (value: number) => void;
  viewMode?: 'table' | 'card';
  onViewModeChange?: (mode: 'table' | 'card') => void;
}

const TIME_RANGE_OPTIONS = [
  { id: 'TODAY', name: strings.todayText || 'Today' },
  { id: 'THIS_WEEK', name: `This ${strings.weekText || 'Week'}` },
  { id: 'THIS_MONTH', name: `This ${strings.monthText || 'Month'}` },
];

const viewModeToggleSx = {
  flexShrink: 0,
  height: 36,
  '& .MuiToggleButton-root': {
    px: 0,
    py: 0,
    width: 36,
    minWidth: 36,
    height: 36,
  },
  '& .MuiToggleButton-root.Mui-selected': {
    fontWeight: 700,
  },
};

const SalesCallsList: FC<SalesCallsListProps> = ({
  tabValue: tabValueProp,
  assignees = [],
  enableCardView = true,
  onTabChange,
  viewMode,
  onViewModeChange,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [localTabValue, setLocalTabValue] = useState(0);
  const isTabControlled = tabValueProp !== undefined;
  const tabValue = isTabControlled ? tabValueProp : localTabValue;
  const handleTabChange = useCallback(
    (_: React.SyntheticEvent, newValue: number) => {
      if (isTabControlled) {
        onTabChange?.(newValue);
      } else {
        setLocalTabValue(newValue);
      }
    },
    [isTabControlled, onTabChange],
  );
  const index = useSelector(pageNo) || 0;
  const filter = useSelector(filterDataforApi);
  const pageSize = useSelector(rowCount) || 10;
  const currentFilterData = useSelector(filterData);
  const userPrivileges = useSelector(Privilege);
  const canEdit = userPrivileges?.includes(privileges.edit_sales_call);
  const canDelete = userPrivileges?.includes(privileges.delete_sales_call);

  const [filterDropdowns, setFilterDropdowns] = useState<any>({});
  const [pageResponse, setPageResponse] = useState<any>({ content: [], totalElements: 0 });
  const [loading, setLoading] = useState<boolean>(false);

  // State for follow-up modal
  const [followUpModalOpen, setFollowUpModalOpen] = useState(false);
  const [activeSalesCallId, setActiveSalesCallId] = useState<number | null>(null);

  // State for daily sales report modal
  const [dailySalesReportModalOpen, setDailySalesReportModalOpen] = useState(false);
  const [rowInfo, setRowInfo] = useState<any[]>([]);
  const [dsrTabValue, setDsrTabValue] = useState(0);
  const [dsrComments, setDsrComments] = useState<any[]>([]);
  const [dsrFollowUps, setDsrFollowUps] = useState<any[]>([]);

  // State for comment modal
  const [commentModalOpen, setCommentModalOpen] = useState(false);
  const [activeCommentSalesCallId, setActiveCommentSalesCallId] = useState<number | null>(null);

  // State for delete confirmation modal
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [salesCallToDelete, setSalesCallToDelete] = useState<number | null>(null);

  // State for follow-up expansion
  const [selectedDateRange, setSelectedDateRange] = useState<string>('');
  const [selectedManagedBy, setSelectedManagedBy] = useState<string | number>('');

  const [localViewMode, setLocalViewMode] = useState<'table' | 'card'>('table');
  const isViewModeControlled = viewMode !== undefined;
  const currentViewMode = (isViewModeControlled ? viewMode : localViewMode) ?? 'table';

  const handleViewModeChange = useCallback(
    (mode: 'table' | 'card') => {
      if (!enableCardView && mode === 'card') return;
      if (isViewModeControlled) {
        onViewModeChange?.(mode);
      } else {
        setLocalViewMode(mode);
      }
    },
    [enableCardView, isViewModeControlled, onViewModeChange],
  );
  const [message, setMessage] = useState<{
    display: boolean;
    severity: AlertColor | null;
    message: string;
  }>({ display: false, severity: null, message: '' });

  const closeNotification = (value: boolean) => setMessage({ ...message, display: value });

  const handleExportSalesCalls = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(URLS.exportSalesCalls, {
        responseType: 'blob',
      });
      downloadExportFile(response, 'xlsx', 'Sales_Calls');
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      setMessage({
        display: true,
        severity: 'error',
        message: error?.response?.data?.message || 'Failed to export sales calls',
      });
    }
  }, []);

  const fetchFilterDropdowns = useCallback(async () => {
    try {
      const [companyFiltersResponse, followUpDropdownResponse] = await Promise.all([
        axiosInstance.get(URLS.getCompanyFilters),
        axiosInstance.get(URLS.followUpDropdown),
      ]);

      setFilterDropdowns({
        primaryManagers: createDropdownData(companyFiltersResponse.data.primaryManagers ?? [], ['id', 'name']),
        followUpModes: createDropdownData(followUpDropdownResponse.data?.followUpModes ?? [], ['id', 'name']),
        assignees: createDropdownData(followUpDropdownResponse.data?.assignees ?? [], ['id', 'name']),
      });
    } catch (error: any) {
      console.log('Error fetching filter dropdowns:', error);
    }
  }, []);

  useEffect(() => {
    fetchFilterDropdowns();
  }, [fetchFilterDropdowns]);

  const getList = useCallback(
    async (filterData?: PageProps) => {
      const body = filterData || {
        page: index + 1,
        size: pageSize,
        filter: filter,
        sort: ['createdAt:desc'],
      };

      try {
        setLoading(true);
        let url = URLS.salesCallPage(tabValue === 1);
        if (tabValue === 2) {
          url = `${URLS.salesCallPage(false)}&showColdCalls=true`;
        }
        const { data } = await axiosInstance.post(url, body);
        const normalizedContent = (data.content || []).map((item: any) => ({
          ...item,
          nextFollowUp: Array.isArray(item.nextFollowUp)
            ? item.nextFollowUp
            : item.nextFollowUp
            ? [item.nextFollowUp]
            : [],
        }));
        setPageResponse({ ...data, content: normalizedContent });
        setLoading(false);
      } catch (error: any) {
        setLoading(false);
        setMessage({
          display: true,
          severity: 'error',
          message: error.response?.data?.message || 'Failed to fetch sales calls',
        });
      }
    },
    [index, pageSize, filter, tabValue],
  );

  useEffect(() => {
    getList();
  }, [getList]);

  useEffect(() => {
    if (!enableCardView && currentViewMode !== 'table') {
      handleViewModeChange('table');
    }
  }, [enableCardView, currentViewMode, handleViewModeChange]);

  // Handle managed by change
  const applyHeaderFilter = useCallback(
    (updatedFilter: Record<string, any>) => {
      const filterArray: string[] = [];
      Object.keys(updatedFilter).forEach(key => {
        if (updatedFilter[key]) {
          if (filterArray.length > 0) {
            filterArray.push('and');
          }
          filterArray.push(`${key}:cic:${updatedFilter[key]}`);
        }
      });

      dispatch(setFilter(updatedFilter));
      dispatch(setFilterDataForApi(filterArray));
      dispatch(setPageNo(0));
      getList({
        page: 1,
        size: pageSize,
        filter: filterArray,
        sort: ['createdAt:desc'],
      });
    },
    [dispatch, getList, pageSize],
  );

  const handleManagedByChange = useCallback(
    (value: string | number) => {
      setSelectedManagedBy(value);
      applyHeaderFilter({
        ...currentFilterData,
        managedBy: value || '',
      });
    },
    [applyHeaderFilter, currentFilterData],
  );

  const handleDateRangeFilter = useCallback(
    (range: string) => {
      if (!range) {
        const { startDate, endDate, ...restFilter } = currentFilterData;
        setSelectedDateRange('');
        applyHeaderFilter(restFilter);
        return;
      }

      const today = dayjs();
      let start: dayjs.Dayjs;
      let end: dayjs.Dayjs;

      switch (range) {
        case 'TODAY':
          start = today.startOf('day');
          end = today.endOf('day');
          break;
        case 'THIS_WEEK':
          start = today.startOf('week');
          end = today.endOf('week');
          break;
        case 'THIS_MONTH':
          start = today.startOf('month');
          end = today.endOf('month');
          break;
        default:
          return;
      }

      setSelectedDateRange(range);
      applyHeaderFilter({
        ...currentFilterData,
        startDate: start.format('YYYY-MM-DD'),
        endDate: end.format('YYYY-MM-DD'),
        ...(tabValue === 1 && selectedManagedBy ? { managedBy: selectedManagedBy } : {}),
      });
    },
    [applyHeaderFilter, currentFilterData, selectedManagedBy, tabValue],
  );

  const handleClearAllHeaderFilters = useCallback(() => {
    setSelectedDateRange('');
    setSelectedManagedBy('');
  }, []);

  // Handler to open follow-up modal
  const closeFollowUpModal = () => {
    setFollowUpModalOpen(false);
  };

  // Handler to refresh after follow-up added
  const handleFollowUpSuccess = async () => {
    closeFollowUpModal();
    getList();
    if (dailySalesReportModalOpen && activeSalesCallId) {
      try {
        const [dsrData, salesCallData] = await Promise.all([
          axiosInstance.get(URLS.getDailySalesReport(activeSalesCallId)),
          axiosInstance.get(URLS.fetchSalesCallById(activeSalesCallId)).catch(() => ({ data: null })),
        ]);
        setDsrFollowUps(dsrData.data.tasks || salesCallData.data?.tasks || []);
        setDsrComments(dsrData.data.comments || salesCallData.data?.comments || []);
      } catch (error) {
        console.log(error);
      }
    }
  };

  // Handler to open follow-up modal
  const openFollowUpModal = (salesCallId: number) => {
    setActiveSalesCallId(salesCallId);
    setFollowUpModalOpen(true);
  };

  // Handler to open comment modal
  const openCommentModal = (salesCallId: number) => {
    setActiveCommentSalesCallId(salesCallId);
    setCommentModalOpen(true);
  };

  // Handler to close comment modal
  const closeCommentModal = () => {
    setCommentModalOpen(false);
    setActiveCommentSalesCallId(null);
  };

  // Handler to refresh comments
  const refreshComments = async () => {
    getList(); // Refresh the main list
    // Refresh DSR data if DSR drawer is open
    if (dailySalesReportModalOpen && activeSalesCallId) {
      try {
        const [dsrData, salesCallData] = await Promise.all([
          axiosInstance.get(URLS.getDailySalesReport(activeSalesCallId)),
          axiosInstance.get(URLS.fetchSalesCallById(activeSalesCallId)).catch(() => ({ data: null })),
        ]);
        setDsrComments(dsrData.data.comments || salesCallData.data?.comments || []);
        setDsrFollowUps(dsrData.data.tasks || salesCallData.data?.tasks || []);
      } catch (error) {
        console.log(error);
      }
    }
  };

  // Daily sales report handlers
  const handleDailySalesReport = useCallback(async (id: number, isFlag?: boolean) => {
    try {
      setActiveSalesCallId(id);
      const [dsrResponse, salesCallResponse] = await Promise.all([
        axiosInstance.get(URLS.getDailySalesReport(id)),
        axiosInstance.get(URLS.fetchSalesCallById(id)).catch(() => ({ data: null, status: 200 })),
      ]);

      if (dsrResponse.status === 200) {
        const dsrData = dsrResponse.data;
        const salesCallData = salesCallResponse.data;

        const mergedData = {
          ...(salesCallData || {}),
          ...dsrData,
          comments: dsrData.comments || salesCallData?.comments || [],
          tasks: dsrData.tasks || salesCallData?.tasks || [],
        };

        const resolveValue = (value: any): string => {
          if (value === null || value === undefined) return '-';
          if (typeof value === 'string' || typeof value === 'number') return `${value}`;
          if (Array.isArray(value)) {
            const formatted = value
              .map(item => {
                if (typeof item === 'string' || typeof item === 'number') return item;
                if (typeof item === 'object') {
                  return item.fullName || item.name || item.label || item.value || null;
                }
                return null;
              })
              .filter(Boolean);
            return formatted.length ? formatted.join(', ') : '-';
          }
          if (typeof value === 'object') {
            return value.fullName || value.name || value.label || value.value || '-';
          }
          return '-';
        };

        const formatTimelineItems = (items: any[] = [], options?: { showStatus?: boolean }) =>
          items.map(item => {
            const createdAt = item.createdAt || item.datetime || item.updatedAt;
            return {
              id: item.id,
              date: createdAt ? dayjs(createdAt).format('DD MMM YYYY') : '-',
              time: createdAt ? dayjs(createdAt).format('hh:mm A') : '',
              summary: item.comment ?? item.description ?? '',
              status: options?.showStatus ? item.status : undefined,
              createdBy: item.createdBy ? resolveValue(item.createdBy) : '',
              attachmentUrl: item.attachmentUrl,
            } as TimelineItem;
          });

        setRowInfo([
          { label: strings.sales_call_created_by, value: resolveValue(mergedData?.createdBy) },
          { label: strings.lead, value: resolveValue(mergedData?.companyMaster ?? mergedData?.companyName) },
          {
            label: strings.sales_company_branch_name,
            value: resolveValue(mergedData?.companyBranch ?? mergedData?.branchName),
          },
          { label: strings.sales_report_customer_name, value: resolveValue(mergedData?.contactedWith) },
          { label: strings.sales_report_type_of_Call, value: resolveValue(mergedData?.callType) },
          { label: strings.sales_report_call_summary, value: resolveValue(mergedData?.discussionSummary) },
          {
            label: strings.sales_report_follow_up_date,
            value:
              mergedData?.tasks
                ?.map(
                  (task: { createdAt: string; status: string }) =>
                    `${dayjs(task.createdAt).format('DD-MM-YYYY')} - ${task.status}`,
                )
                ?.join(', ') || '-',
          },
          { label: strings.sales_report_business_secured, value: resolveValue(mergedData?.businessSecured) },
        ]);

        setDsrFollowUps(formatTimelineItems(mergedData.tasks, { showStatus: true }));
        setDsrComments(formatTimelineItems(mergedData.comments));
        setDsrTabValue(0);
        setDailySalesReportModalOpen(true);
      }
    } catch (error) {
      console.log(error);
      setMessage({
        display: true,
        severity: 'error',
        message: 'Failed to fetch daily sales report',
      });
    }
  }, []);

  const closeDailySalesReportModal = () => {
    setDailySalesReportModalOpen(false);
    setRowInfo([]);
    setActiveSalesCallId(null);
  };

  const tableRows = useMemo(() => pageResponse?.content ?? [], [pageResponse]);

  const handleEditSalesCall = (id: number) => {
    navigate(`${routes.editSalesManagement}/${id}`);
  };

  const handleDeleteSalesCall = (id: number) => {
    setSalesCallToDelete(id);
    setDeleteConfirmationOpen(true);
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    dispatch(setPageNo(newPage));
  };

  const handleRowsPerPageChange = (event: SelectChangeEvent<any>) => {
    const value = parseInt(event.target.value as string, 10);
    dispatch(setPageNo(0));
    dispatch(setRowCount(value));
  };

  const pageConfig: PageConfigModel = useMemo(
    () => ({
      title: strings.salesCalls,
      entityLabel: strings.salesCalls,
      subtitle: strings.sales_calls_list_subtitle,
      searchField: 'search',
      searchPlaceholder: 'Search by company, contact, title, or prospect',
      AddButtonText: strings.sales_calls_add_button || 'Add Sales Call',
      AddButtonUrl:
        tabValue === 2 ? `${routes.addSalesManagement}?callingFor=NEW_PROSPECT` : routes.addSalesManagement,
      addPrivilege: privileges.add_sales_call,
      deletePrivilege: privileges.delete_sales_call,
      editPrivilege: privileges.edit_sales_call,
      editButtonUrl: routes.editSalesManagement,
      deleteApi: URLS.fetchSalesCallById,
      exportPrivilege: privileges.view_sales_call,
      exportIconOnly: true,
      customExport: handleExportSalesCalls,
      customComponent: enableCardView
        ? () => (
            <ToggleButtonGroup
              exclusive
              value={currentViewMode}
              onChange={(_event, value: 'table' | 'card' | null) => {
                if (value) handleViewModeChange(value);
              }}
              size="small"
              aria-label="Sales call view"
              sx={viewModeToggleSx}>
              <ToggleButton value="table" aria-label="List view" title="List">
                <ViewListOutlined fontSize="small" />
              </ToggleButton>
              <ToggleButton value="card" aria-label="Tiles view" title="Tiles">
                <GridViewOutlined fontSize="small" />
              </ToggleButton>
            </ToggleButtonGroup>
          )
        : undefined,
      headerFilters: () => (
        <>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel sx={{ fontSize: 12 }}>Time range</InputLabel>
            <Select
              label="Time range"
              value={selectedDateRange || ''}
              onChange={event => handleDateRangeFilter(String(event.target.value))}
              sx={{
                height: 36,
                fontSize: 13,
                bgcolor: '#fff',
                '& .MuiSelect-select': { py: 0.75 },
              }}>
              <MenuItem value="">All time</MenuItem>
              {TIME_RANGE_OPTIONS.map(option => (
                <MenuItem key={option.id} value={option.id} sx={{ fontSize: 13 }}>
                  {option.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {tabValue === 1 && filterDropdowns?.primaryManagers?.length > 0 ? (
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel sx={{ fontSize: 12 }}>{strings.managedBy}</InputLabel>
              <Select
                label={strings.managedBy}
                value={selectedManagedBy || ''}
                onChange={event => handleManagedByChange(event.target.value)}
                sx={{
                  height: 36,
                  fontSize: 13,
                  bgcolor: '#fff',
                  '& .MuiSelect-select': { py: 0.75 },
                }}>
                <MenuItem value="">All managers</MenuItem>
                {filterDropdowns.primaryManagers.map((item: any) => (
                  <MenuItem key={item.value} value={item.value} sx={{ fontSize: 13 }}>
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : null}
        </>
      ),
      filterChips: [
        ...(selectedDateRange
          ? [
              {
                key: 'timeRange',
                label: `Time range : ${
                  TIME_RANGE_OPTIONS.find(option => option.id === selectedDateRange)?.name || selectedDateRange
                }`,
              },
            ]
          : []),
        ...(tabValue === 1 && selectedManagedBy
          ? [
              {
                key: 'managedBy',
                label: `Managed by : ${
                  filterDropdowns?.primaryManagers?.find((item: any) => String(item.value) === String(selectedManagedBy))
                    ?.label || selectedManagedBy
                }`,
              },
            ]
          : []),
      ],
      onClearFilterChip: (key: string) => {
        if (key === 'timeRange') handleDateRangeFilter('');
        if (key === 'managedBy') handleManagedByChange('');
      },
      onClearAllFilterChips: handleClearAllHeaderFilters,
      toolbarComponent: () => (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
          <StyledTabs
            compact
            tabs={[
              { label: strings.mySalesCalls },
              { label: strings.myTeamSalesCalls },
              { label: strings.coldCalls },
            ]}
            value={tabValue}
            onChange={handleTabChange}
            aria-label="sales calls tabs"
          />
        </Box>
      ),
      tableColumn: [
        {
          field: 'contactedWith',
          headerName: 'Contact',
          flex: 2.2,
          sortable: false,
          renderCell: (_value: any, row: any) => <SalesCallContactCell row={row} />,
        },
        {
          field: 'createdAt',
          headerName: strings.bookingHistoryTabsDate || 'Date',
          flex: 0.9,
          sortable: true,
          renderCell: (_value: any, row: any) => <SalesCallDateCell row={row} />,
        },
        {
          field: 'followUpMode',
          headerName: 'Type',
          flex: 0.8,
          sortable: true,
          renderCell: (value: any) => formatSalesCallType(value),
        },
        {
          field: 'title',
          headerName: strings.title || 'Title',
          flex: 2.4,
          sortable: true,
          renderCell: (value: any) => {
            const title = value || '—';
            return (
              <Tooltip title={title === '—' ? '' : title}>
                <Typography
                  sx={{
                    fontSize: '0.8125rem',
                    lineHeight: 1.35,
                    fontWeight: 500,
                    minWidth: 0,
                    maxWidth: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>
                  {title}
                </Typography>
              </Tooltip>
            );
          },
        },
        {
          field: 'status',
          headerName: strings.bookingHistoryTabsEditedStatus || 'Status',
          flex: 0.8,
          sortable: false,
          renderCell: (_value: any, row: any) => <SalesCallStatusChip row={row} />,
        },
        ...(tabValue === 1
          ? [
              {
                field: 'assignedTo',
                headerName: 'Assigned',
                flex: 0.55,
                sortable: false,
                renderCell: (_value: any, row: any) => <SalesCallAssigneeAvatar row={row} />,
              },
            ]
          : []),
      ],
      actionMenuItems: [
        {
          materialIcon: <BadgeOutlined fontSize="small" />,
          tooltip: strings.sales_report || 'Daily Sales Report',
          privilege: privileges.view_sales_call || privileges.edit_sales_call,
          handleCustomImageMethod: (id: number) => handleDailySalesReport(id, true),
        },
        {
          materialIcon: <ChatBubbleOutline fontSize="small" />,
          tooltip: strings.sales_calls_add_follow_up || 'Add Follow-up',
          privilege: privileges.add_sales_call || '',
          handleCustomImageMethod: (id: number) => openFollowUpModal(id),
        },
        {
          materialIcon: <Comment fontSize="small" />,
          tooltip: strings.sales_calls_add_comment || 'Add Comment',
          privilege: privileges.add_sales_call || '',
          handleCustomImageMethod: (id: number) => openCommentModal(id),
        },
      ],
      hideDefaultEditAction: false,
      hideDefaultDeleteAction: false,
    }),
    [
      tabValue,
      enableCardView,
      currentViewMode,
      handleViewModeChange,
      handleExportSalesCalls,
      selectedDateRange,
      handleDateRangeFilter,
      selectedManagedBy,
      handleManagedByChange,
      filterDropdowns?.primaryManagers,
      handleTabChange,
      handleDailySalesReport,
      handleClearAllHeaderFilters,
    ],
  );

  const closeDeleteModal = () => {
    setDeleteConfirmationOpen(false);
    setSalesCallToDelete(null);
  };

  return (
    <>
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={closeNotification}
        />
      )}

      <ListPage
        pageConfig={pageConfig}
        pageResponse={{
          ...pageResponse,
          content: tableRows,
        }}
        getList={getList}
        isLoading={loading}
        hideTable={enableCardView && currentViewMode === 'card'}
      />

      {enableCardView && currentViewMode === 'card' && !loading && pageResponse?.content?.length > 0 ? (
        <>
          <Grid container spacing={3} sx={{ mt: 0.5 }}>
            {pageResponse.content.map((call: any) => (
              <Grid item xs={12} md={6} lg={4} key={call.id}>
                <SalesCallCard
                  data={call}
                  onDailySalesReport={handleDailySalesReport}
                  onFollowUp={openFollowUpModal}
                  onComment={openCommentModal}
                  onEdit={handleEditSalesCall}
                  onDelete={handleDeleteSalesCall}
                  canEdit={canEdit}
                  canDelete={canDelete}
                  showAssignee={tabValue === 1}
                />
              </Grid>
            ))}
          </Grid>
          <Box
            sx={{
              mt: 3,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderTop: theme => `1px solid ${theme.palette.divider}`,
              pt: 2,
            }}>
            <TablePaginationAction
              count={pageResponse.totalElements || 0}
              rowsPerPage={pageSize}
              page={index}
              onRowsPerPageChange={handleRowsPerPageChange}
              onPageChange={handleChangePage}
            />
            <Typography variant="caption" color="text.secondary">
              Page {index + 1} of {Math.max(1, Math.ceil((pageResponse.totalElements || 0) / pageSize))}
            </Typography>
          </Box>
        </>
      ) : enableCardView && currentViewMode === 'card' && !loading ? (
        <Box
          sx={{
            py: 8,
            border: theme => `1px dashed ${theme.palette.divider}`,
            borderRadius: 3,
            textAlign: 'center',
          }}>
          <Typography variant="h6">{strings.no_data_available || 'No sales calls found'}</Typography>
          <Typography variant="body2" color="text.secondary">
            Try adjusting your filters or add a new sales call.
          </Typography>
        </Box>
      ) : null}

      {followUpModalOpen && (
        <SalesCallFollowUpForm
          open={followUpModalOpen}
          onClose={closeFollowUpModal}
          salesCallId={activeSalesCallId as number}
          assignees={filterDropdowns?.assignees || assignees}
          followUpModes={filterDropdowns?.followUpModes || []}
          onSuccess={handleFollowUpSuccess}
        />
      )}
      <DrawerMenu
        width={'60vw'}
        open={dailySalesReportModalOpen}
        handleClose={closeDailySalesReportModal}
        closeButton={true}
        title="Daily Sales Report"
        dropBack={true}>
        <Box width="100%" sx={{ p: 2 }}>
          <Tabs
            value={dsrTabValue}
            onChange={(_, newValue) => setDsrTabValue(newValue)}
            sx={{
              mb: 2,
              borderBottom: 1,
              borderColor: 'divider',
              '& .MuiTab-root': {
                borderBottom: '2px solid transparent',
                textTransform: 'capitalize',
                '&.Mui-selected': {
                  borderBottom: '2px solid',
                  borderColor: 'primary.main',
                },
              },
            }}>
            <Tab label="Daily Sales Report" />
            <Tab label="Follow-up / Timeline" />
            <Tab label="Comments" />
          </Tabs>

          {dsrTabValue === 0 && rowInfo?.length > 0 && <SalesDetails formDefaultData={rowInfo} />}

          {dsrTabValue === 1 && (
            <SalesCallTimelineSection
              items={dsrFollowUps}
              emptyText="No follow-ups available"
              showStatus={true}
              onAddClick={() => {
                if (activeSalesCallId) {
                  openFollowUpModal(activeSalesCallId);
                }
              }}
              addButtonText="Add Follow-up"
            />
          )}

          {dsrTabValue === 2 && (
            <SalesCallTimelineSection
              items={dsrComments}
              emptyText={'No comments available'}
              onAddClick={() => {
                if (activeSalesCallId) {
                  openCommentModal(activeSalesCallId);
                }
              }}
              addButtonText="Add Comments"
            />
          )}
        </Box>
      </DrawerMenu>

      {/* Comment Modal */}
      <CommentDrawer
        open={commentModalOpen}
        onClose={closeCommentModal}
        salesCallId={activeCommentSalesCallId}
        onSuccess={refreshComments}
      />

      {deleteConfirmationOpen && salesCallToDelete && (
        <Delete
          openModal={deleteConfirmationOpen}
          closeModal={closeDeleteModal}
          deleteUrl={URLS.fetchSalesCallById(salesCallToDelete)}
          updateList={getList}
        />
      )}
    </>
  );
};

export default SalesCallsList;
